import stateCityData from '../data/state_city.json';

/**
 * Normalize API client document → shared UI / proposal shape
 */
export function mapClientFromApi(raw) {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const r = raw.data && typeof raw.data === 'object' ? raw.data : raw;
  const companyName = r.client_name || r.company_name || '';
  const address = r.company_address || r.address || r.full_address?.line1 || '';
  const city = r.city || r.full_address?.city || '';
  const state = r.state || r.full_address?.state || '';
  const companyEmail = r.client_email || r.email || r.company_email || '';
  const concernedPersonName =
    r.contact_person_name || r.concerned_person_name || r.concernedPersonName || '';
  const concernedPersonMobile =
    r.contact_person_mobile || r.client_mobile || r.concerned_person_mobile || '';
  const companyPhone =
    (typeof r.client_primary_phone === 'string' && r.client_primary_phone.trim()) ||
    (typeof r.client_phone === 'string' && r.client_phone.trim()) ||
    '';
  const concernedPersonEmail = r.contact_person_email || r.concerned_person_email || '';
  const concernedPersonDesignation =
    r.contact_person_designation || r.concerned_person_designation || '';
  const dateOfDiscussion = r.discussion_date || r.date_of_discussion || '';

  const label = [companyName, city, state].filter(Boolean).join(' · ') || companyName || 'Client';

  return {
    id: r._id || r.id || null,
    companyName,
    address,
    city,
    state,
    companyEmail,
    companyPhone,
    concernedPersonName,
    concernedPersonMobile,
    concernedPersonEmail,
    concernedPersonDesignation,
    dateOfDiscussion,
    label,
    raw: r,
  };
}

/**
 * UI client → quotation "client" block for PDF / From-To page
 */
export function mapClientToQuotationFormat(client) {
  if (!client) {
    return {
      company: '',
      location: '',
      kindAttn: '',
      address: '',
      email: '',
      phone: '',
      concernedPersonMobile: '',
      concernedPersonEmail: '',
      concernedPersonDesignation: '',
    };
  }
  const locationParts = [client.city, client.state].filter(Boolean);
  const location = locationParts.length ? locationParts.join(', ') : '';
  const companyPhone = (client.companyPhone || '').trim();
  const contactMobile = (client.concernedPersonMobile || '').trim();
  const mainPhone = companyPhone || contactMobile || '';
  return {
    company: client.companyName || '',
    location,
    kindAttn: client.concernedPersonName || '',
    address: client.address || '',
    email: client.companyEmail || '',
    phone: mainPhone,
    concernedPersonMobile: client.concernedPersonMobile || '',
    concernedPersonEmail: client.concernedPersonEmail || '',
    concernedPersonDesignation: client.concernedPersonDesignation || '',
  };
}

/**
 * Map UI client shape → ClientAddForm initial state
 */
export function clientToAddFormValues(client) {
  if (!client) return null;
  return {
    companyName: client.companyName || '',
    companyAddress: client.address || '',
    city: client.city || '',
    state: client.state || '',
    companyEmail: client.companyEmail || '',
    concernedPersonName: client.concernedPersonName || '',
    concernedPersonMobile: client.concernedPersonMobile || '',
    concernedPersonEmail: client.concernedPersonEmail || '',
    concernedPersonDesignation: client.concernedPersonDesignation || '',
    dateOfDiscussion: client.dateOfDiscussion || '',
  };
}

/**
 * Merge form submit values into existing UI client (local edit without API).
 */
export function mergeClientFromFormValues(existing, trimmed) {
  const label = [trimmed.companyName, trimmed.city, trimmed.state].filter(Boolean).join(' · ') || trimmed.companyName;
  return {
    ...existing,
    companyName: trimmed.companyName,
    address: trimmed.companyAddress,
    city: trimmed.city,
    state: trimmed.state,
    companyEmail: trimmed.companyEmail,
    companyPhone: existing?.companyPhone || '',
    concernedPersonName: trimmed.concernedPersonName,
    concernedPersonMobile: trimmed.concernedPersonMobile,
    concernedPersonEmail: trimmed.concernedPersonEmail,
    concernedPersonDesignation: trimmed.concernedPersonDesignation,
    dateOfDiscussion: trimmed.dateOfDiscussion,
    label,
  };
}

/** Location line similar to legacy quotation style, e.g. PATNA (BIHAR) */
export function formatAddresseeLocationLine(client) {
  if (!client) return '';
  const city = (client.city || '').trim();
  const state = (client.state || '').trim();
  if (city && state) return `${city.toUpperCase()}(${state.toUpperCase()})`;
  if (city) return city.toUpperCase();
  if (state) return state.toUpperCase();
  return '';
}

/**
 * Patch "Addressee and Subject" section content with selected client (plant proposal PDF).
 */
export function patchAddresseeSection(sections, uiClient) {
  if (!Array.isArray(sections) || !uiClient) return sections;
  const company = (uiClient.companyName || '').trim();
  const loc = formatAddresseeLocationLine(uiClient);
  const name = (uiClient.concernedPersonName || '').trim();
  const greeting = name ? `Dear ${name},` : 'Dear Sir,';

  return sections.map((s) => {
    if (s.title !== 'Addressee and Subject' || !s.hierarchy?.content) return s;
    const content = [...s.hierarchy.content];
    if (content.length > 0) content[0] = company;
    if (content.length > 1) content[1] = loc;
    if (content.length > 3) content[3] = greeting;
    return {
      ...s,
      hierarchy: { ...s.hierarchy, content },
    };
  });
}

let cityOptionsMemo = null;

/**
 * Flat list for city Autocomplete: each option knows its state (auto-fill).
 */
export function buildCityStateOptions() {
  if (cityOptionsMemo) return cityOptionsMemo;
  const { states = [], city = {} } = stateCityData;
  const options = [];
  states.forEach((st) => {
    const list = city[String(st.id)] || [];
    list.forEach((ct) => {
      options.push({
        key: `${st.id}-${ct.id}`,
        cityName: ct.name,
        stateId: st.id,
        stateName: st.name,
        label: `${ct.name}, ${st.name}`,
      });
    });
  });
  options.sort((a, b) => a.label.localeCompare(b.label));
  cityOptionsMemo = options;
  return options;
}

export function getStateNamesSorted() {
  const { states = [] } = stateCityData;
  return [...states].sort((a, b) => a.name.localeCompare(b.name));
}
